<script setup lang="ts">
const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  class: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const { $tiptap } = useNuxtApp()
const editorElement = ref<HTMLElement | null>(null)
let editor: any = null
const showLinkDialog = ref(false)
const linkUrl = ref('')
const selectedText = ref('')

onMounted(() => {
  if (editorElement.value) {
    editor = $tiptap.createEditor(editorElement.value, props.modelValue || '')

    editor.on('update', () => {
      let html = editor.getHTML()
      // Remove títulos e transforma em parágrafos
      html = html.replace(/<h[1-6]( [^>]*)?>/gi, '<p>')
      html = html.replace(/<\/h[1-6]>/gi, '</p>')
      // remove line breaks
      html = html.replace(/<br\s*\/?>/gi, '')
      emit('update:modelValue', html)
    })

    // Intercepta o evento de colar
  }
})

onBeforeUnmount(() => {
  if (editor) {
    editor.destroy()
  }
})

// Important: This ensures the editor updates when the model value changes from outside
watch(() => props.modelValue, (newValue) => {
  // Only update content if the editor exists and the content is different
  if (editor && newValue !== editor.getHTML()) {
    console.log('Updating editor content to:', newValue)
    editor.commands.setContent(newValue || '')
  }
}, { immediate: true })

function openLinkDialog () {
  if (!editor) { return }

  // Get currently selected text
  const { state } = editor
  selectedText.value = state.doc.textBetween(
    state.selection.from,
    state.selection.to,
    ''
  )

  // If there's an active link, get its URL
  if (editor.isActive('link')) {
    linkUrl.value = editor.getAttributes('link').href || ''
  } else {
    linkUrl.value = ''
  }

  showLinkDialog.value = true
}

function saveLink () {
  if (!editor || !linkUrl.value) {
    showLinkDialog.value = false
    return
  }

  // If no text is selected, but a URL is entered, use the URL as the link text
  if (!selectedText.value && linkUrl.value) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'text',
        text: linkUrl.value,
      })
      .run()

    editor
      .chain()
      .focus()
      .extendMarkRange('text')
      .setLink({ href: ensureHttpPrefix(linkUrl.value), target: '_blank' })
      .run()
  } else {
    // Otherwise, convert the selected text to a link
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: ensureHttpPrefix(linkUrl.value), target: '_blank' })
      .run()
  }

  showLinkDialog.value = false
  linkUrl.value = ''
}

function ensureHttpPrefix (url: string) {
  if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
    return 'https://' + url
  }
  return url
}

function removeLink () {
  if (!editor) { return }

  editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .unsetLink()
    .run()

  showLinkDialog.value = false
  linkUrl.value = ''
}

function cancelLink () {
  showLinkDialog.value = false
  linkUrl.value = ''
}

defineExpose({
  focus: () => editor?.chain().focus().run(),
  editor: () => editor,
})
</script>

<template>
  <div class="rich-text-editor" :class="props.class">
    <div class="editor-toolbar">
      <button
        type="button"
        class="toolbar-button"
        :class="{ 'is-active': editor?.isActive('bold') }"
        title="Negrito"
        @click="editor?.chain().focus().toggleBold().run()"
      >
        <font-awesome-icon icon="fa-solid fa-bold" />
      </button>
      <button
        type="button"
        class="toolbar-button"
        :class="{ 'is-active': editor?.isActive('italic') }"
        title="Itálico"
        @click="editor?.chain().focus().toggleItalic().run()"
      >
        <font-awesome-icon icon="fa-solid fa-italic" />
      </button>
      <button
        type="button"
        class="toolbar-button"
        :class="{ 'is-active': editor?.isActive('underline') }"
        title="Sublinhado"
        @click="editor?.chain().focus().toggleUnderline().run()"
      >
        <font-awesome-icon icon="fa-solid fa-underline" />
      </button>
      <button
        type="button"
        class="toolbar-button"
        :class="{ 'is-active': editor?.isActive('link') }"
        title="Link"
        @click="openLinkDialog()"
      >
        <font-awesome-icon icon="fa-solid fa-link" />
      </button>
    </div>
    <div ref="editorElement" class="editor-content" />

    <!-- Link Dialog -->
    <div v-if="showLinkDialog" class="link-dialog">
      <div class="link-dialog-content">
        <h5>Inserir Link</h5>
        <div class="link-form">
          <label for="linkUrl">URL:</label>
          <input
            id="linkUrl"
            v-model="linkUrl"
            type="text"
            placeholder="https://www.example.com"
            class="form-control"
            @keyup.enter="saveLink"
          >
        </div>
        <div class="link-dialog-actions">
          <button class="btn btn-sm btn-secondary" @click="cancelLink">
            Cancelar
          </button>
          <button v-if="editor?.isActive('link')" class="btn btn-sm btn-danger" @click="removeLink">
            Remover
          </button>
          <button class="btn btn-sm btn-primary" @click="saveLink">
            Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rich-text-editor {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  overflow: hidden;
  position: relative;
}

.editor-toolbar {
  display: flex;
  padding: 0.5rem;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
}

.toolbar-button {
  width: 2rem;
  height: 2rem;
  margin-right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  cursor: pointer;
}

.toolbar-button:hover,
.toolbar-button.is-active {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.editor-content {
  height: 120px;
  padding: 0.5rem;
  padding-bottom: 0;
  overflow-y: auto;
  resize: vertical;
}
:deep(:focus-visible){
    outline: none;
}

:deep(.tiptap.ProseMirror){
    padding: 0.5em;
    margin: -0.5em;
    height: 102%;
    resize: vertical;
}
:deep(.tiptap.ProseMirror:focus-visible){
    outline: rgba(0, 123, 255, 0.25) auto 1em;
}

/* Link Dialog */
.link-dialog {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.link-dialog-content {
  background-color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 400px;
}

.link-form {
  margin: 1rem 0;
}

.link-form label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.link-form input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.link-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}

/* Conteúdo gerado pelo editor */
:deep(.editor-content p) {
  margin-top: 6px;
  margin-bottom: 6px;
  line-height: 1;
  font-family: Arial, Roboto, Cambria, sans-serif;
  font-size: 12px;
  text-align: justify;
}

:deep(.editor-content a) {
  color: #0d6efd;
  text-decoration: underline;
}

:deep(.editor-content img) {
  max-width: 100%;
  height: auto;
}
</style>
